import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Trash2, Minus, Plus, ShoppingBag, 
  Truck, Shield, Tag, ArrowRight
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import Navbar from '@/components/layout/Navbar';
import { useLanguage } from '@/i18n/LanguageContext';
import Footer from '@/components/sections/Footer';

const FREE_DELIVERY_THRESHOLD = 14000;

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const { t } = useLanguage();
  
  const subtotal = getCartTotal();
  const deliveryFee = subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : 500;
  const total = subtotal + deliveryFee;
  const amountForFreeDelivery = FREE_DELIVERY_THRESHOLD - subtotal;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 lg:px-8 py-8 pt-24">
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
              <ShoppingBag className="w-12 h-12 text-muted-foreground" />
            </div>
            <h1 className="font-display text-4xl text-foreground mb-4">{t("cart.empty") || "Your Cart is Empty"}</h1>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              {t("cart.emptyMessage") || "Looks like you haven't added anything to your cart yet."}
            </p>
            <Button variant="jamaica" size="xl" onClick={() => navigate('/shop')}>
              <ShoppingBag className="w-5 h-5" />
              Start Shopping
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="container mx-auto px-4 lg:px-8 py-8 pt-24">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span>/</span>
          <span className="text-foreground">{t("cart.title") || "Shopping Cart"}</span>
        </nav>

        {/* Back Button */}
        <Button variant="ghost" className="mb-6" onClick={() => navigate('/shop')}>
          <ArrowLeft className="w-4 h-4" />
          Continue Shopping
        </Button>

        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl lg:text-4xl text-foreground">
            Shopping Cart ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})
          </h1>
          <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={clearCart}>
            <Trash2 className="w-4 h-4" />
            Clear Cart
          </Button>
        </div>

        {/* Free Delivery Banner */}
        {amountForFreeDelivery > 0 && (
          <div className="bg-secondary/10 rounded-xl p-4 mb-8 flex items-center gap-4">
            <Truck className="w-6 h-6 text-secondary flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-foreground">
                Add <span className="text-primary font-bold">${amountForFreeDelivery.toLocaleString()}</span> more for FREE delivery!
              </p>
              <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-jamaica rounded-full transition-all"
                  style={{ width: `${Math.min(100, (subtotal / FREE_DELIVERY_THRESHOLD) * 100)}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {subtotal >= FREE_DELIVERY_THRESHOLD && (
          <div className="bg-primary/10 rounded-xl p-4 mb-8 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Truck className="w-5 h-5 text-primary-foreground" />
            </div>
            <p className="font-medium text-primary">
              🎉 Congratulations! You qualify for FREE delivery!
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map(item => (
              <Card key={item.id} className="overflow-hidden" data-testid={`cart-item-${item.id}`}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <Link to={`/product/${item.id}`} className="flex-shrink-0">
                      <img 
                        src={item.image || 'https://via.placeholder.com/120'} 
                        alt={item.title}
                        className="w-24 h-24 lg:w-32 lg:h-32 object-cover rounded-lg"
                      />
                    </Link>
                    
                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">{item.seller_name}</p>
                          <Link to={`/product/${item.id}`}>
                            <h3 className="font-semibold text-foreground hover:text-primary transition-colors line-clamp-2">
                              {item.title}
                            </h3>
                          </Link>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                          aria-label="Remove item"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <div className="mt-4 flex items-center justify-between">
                        {/* Quantity Controls */}
                        <div className="flex items-center border rounded-lg">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-2 hover:bg-muted transition-colors"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="px-4 font-medium">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-2 hover:bg-muted transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Price */}
                        <div className="text-right">
                          <p className="font-display text-xl text-primary">
                            ${(item.price * item.quantity).toLocaleString()}
                          </p>
                          {item.quantity > 1 && (
                            <p className="text-xs text-muted-foreground">
                              ${item.price.toLocaleString()} each
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="font-display text-2xl text-foreground mb-6">Order Summary</h2>
                
                {/* Promo Code */}
                <div className="flex gap-2 mb-6">
                  <input 
                    type="text" 
                    placeholder="Promo code"
                    className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Button variant="outline">
                    <Tag className="w-4 h-4" />
                    Apply
                  </Button>
                </div>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium">${subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivery</span>
                    <span className={`font-medium ${deliveryFee === 0 ? 'text-primary' : ''}`}>
                      {deliveryFee === 0 ? 'FREE' : `$${deliveryFee.toLocaleString()}`}
                    </span>
                  </div>
                  <div className="border-t pt-4 flex justify-between">
                    <span className="font-semibold text-lg">Total</span>
                    <span className="font-display text-2xl text-primary">${total.toLocaleString()}</span>
                  </div>
                </div>
                
                <Button 
                  variant="jamaica" 
                  size="xl" 
                  className="w-full mb-4"
                  data-testid="checkout-btn"
                  onClick={() => navigate('/checkout')}
                >
                  Proceed to Checkout
                  <ArrowRight className="w-5 h-5" />
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/shop')}
                >
                  Continue Shopping
                </Button>
                
                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t flex items-center justify-center gap-6 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span className="text-xs">Secure Checkout</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    <span className="text-xs">Fast Delivery</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Cart;
